import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import MessageContent from '../message/MessageContent';
import MapView from '../maps/MapView';

const ChartSection = memo(({ rawResponse }) => {

  const getChartData = () => {
    // Get interpretation data from either location
    const interpretation = rawResponse?.interpretation || rawResponse;
    
    // Special case for map type
    if (interpretation?.suggestedChart === 'map' && interpretation?.map) {
      // Extract map configuration, handling potential typos in the API response
      const { nameColumn, nameLabel } = interpretation.map;
      
      // Handle the typo in longitudeColumn (sometimes returned as longitureColumn)
      const latitudeColumn = interpretation.map.latitudeColumn;
      const longitudeColumn = interpretation.map.longitudeColumn || 
                             interpretation.map.longitureColumn; // Handle typo
      
      // Validate required fields for map
      if (!latitudeColumn || !longitudeColumn || !rawResponse?.results?.rows?.length) {
        return null;
      }
      
      try {
        // Create map data format
        const mapData = {
          markers: []
        };
        
        // Check if rows are arrays or objects
        const firstRow = rawResponse.results.rows[0];
        const rowsAreArrays = Array.isArray(firstRow);
        
        // If rows are arrays, find the column indices
        let nameColumnIndex = -1;
        let latitudeColumnIndex = -1;
        let longitudeColumnIndex = -1;
        
        if (rowsAreArrays) {
          if (rawResponse.results.columns) {
            // Find column indices from the columns array
            nameColumnIndex = rawResponse.results.columns.indexOf(nameColumn);
            latitudeColumnIndex = rawResponse.results.columns.indexOf(latitudeColumn);
            longitudeColumnIndex = rawResponse.results.columns.indexOf(longitudeColumn);
            
            // If we couldn't find the columns by name, try to infer them from the data
            if (latitudeColumnIndex === -1 || longitudeColumnIndex === -1) {
              // Try to find columns that might contain latitude/longitude values
              // by checking if they contain numeric values in the expected ranges
              rawResponse.results.columns.forEach((col, index) => {
                // Skip if we've already found both columns
                if (latitudeColumnIndex !== -1 && longitudeColumnIndex !== -1) return;
                
                // Check a few rows to see if this column contains valid lat/lon values
                const validValues = rawResponse.results.rows.slice(0, 5).filter(row => {
                  const val = parseFloat(row[index]);
                  return !isNaN(val);
                }).length;
                
                // If most values are valid numbers, this might be a coordinate column
                if (validValues >= Math.min(3, rawResponse.results.rows.length)) {
                  // Check if values are in latitude range (-90 to 90)
                  const latValues = rawResponse.results.rows.slice(0, 5).map(row => parseFloat(row[index]))
                    .filter(val => !isNaN(val) && val >= -90 && val <= 90);
                  
                  // Check if values are in longitude range (-180 to 180)
                  const lonValues = rawResponse.results.rows.slice(0, 5).map(row => parseFloat(row[index]))
                    .filter(val => !isNaN(val) && val >= -180 && val <= 180);
                  
                  // If column name contains "lat" or most values are in latitude range
                  if (col.toLowerCase().includes('lat') || 
                      (latValues.length >= Math.min(3, rawResponse.results.rows.length) && latitudeColumnIndex === -1)) {
                    latitudeColumnIndex = index;
                  }
                  // If column name contains "lon" or "lng" or most values are in longitude range
                  else if ((col.toLowerCase().includes('lon') || col.toLowerCase().includes('lng')) || 
                           (lonValues.length >= Math.min(3, rawResponse.results.rows.length) && longitudeColumnIndex === -1)) {
                    longitudeColumnIndex = index;
                  }
                }
              });
              
              // If we still couldn't find the columns, try a simpler approach:
              // For location data, often the first column is the name, second is lat, third is lon
              if (latitudeColumnIndex === -1 && longitudeColumnIndex === -1 && firstRow.length >= 3) {
                // Check if the second and third columns contain numeric values
                const secondColNumeric = !isNaN(parseFloat(firstRow[1]));
                const thirdColNumeric = !isNaN(parseFloat(firstRow[2]));
                
                if (secondColNumeric && thirdColNumeric) {
                  nameColumnIndex = 0;
                  latitudeColumnIndex = 1;
                  longitudeColumnIndex = 2;
                }
              }
            }
          } else {
            // If we don't have column names at all, try to infer from the data directly
            
            // For location data, often the first column is the name, second is lat, third is lon
            if (firstRow.length >= 3) {
              // Check if the second and third columns contain numeric values
              const secondColNumeric = !isNaN(parseFloat(firstRow[1]));
              const thirdColNumeric = !isNaN(parseFloat(firstRow[2]));
              
              if (secondColNumeric && thirdColNumeric) {
                nameColumnIndex = 0;
                latitudeColumnIndex = 1;
                longitudeColumnIndex = 2;
              }
            }
          }
        }
        
        // Process each row to create markers
        const validMarkers = rawResponse.results.rows
          .map(item => {
            // Get values based on whether rows are arrays or objects
            let nameValue, latValue, lonValue;
            
            if (rowsAreArrays) {
              // For array-based rows, use the column indices
              nameValue = nameColumnIndex >= 0 ? item[nameColumnIndex] : null;
              latValue = latitudeColumnIndex >= 0 ? item[latitudeColumnIndex] : null;
              lonValue = longitudeColumnIndex >= 0 ? item[longitudeColumnIndex] : null;
            } else {
              // For object-based rows, use the column names
              nameValue = item[nameColumn];
              latValue = item[latitudeColumn];
              lonValue = item[longitudeColumn];
            }
            
            // Skip invalid rows
            if (!latValue || !lonValue) {
              return null;
            }
            
            // Parse coordinates
            const lat = parseFloat(latValue);
            const lon = parseFloat(lonValue);
            
            // Validate coordinates
            if (isNaN(lat) || isNaN(lon) || 
                lat < -90 || lat > 90 || 
                lon < -180 || lon > 180) {
              return null;
            }
            
            // Create the marker object
            const marker = {
              position: [lat, lon],
              name: nameValue || 'Unnamed Location',
              color: 'black'
            };
            
            return marker;
          })
          .filter(marker => marker !== null);
        
        mapData.markers = validMarkers;
        
        // If we have markers, calculate center and appropriate zoom level
        if (validMarkers.length > 0) {
          // Calculate bounds to determine center and zoom
          let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
          validMarkers.forEach(marker => {
            const lat = marker.position[0];
            const lon = marker.position[1];
            
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLon = Math.min(minLon, lon);
            maxLon = Math.max(maxLon, lon);
          });
          
          // Calculate center based on min/max lat/lon
          mapData.center = [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
          
          // Calculate appropriate zoom level
          const latDiff = maxLat - minLat;
          const lonDiff = maxLon - minLon;
          
          // Adjust longitude difference to account for date line
          const adjustedLonDiff = lonDiff > 180 ? 360 - lonDiff : lonDiff;
          
          // Calculate the maximum span (in degrees) across either latitude or longitude
          const maxSpan = Math.max(latDiff, adjustedLonDiff);
          
          // Set zoom based on the maximum span
          if (maxSpan > 150) mapData.zoom = 1;       // World view
          else if (maxSpan > 120) mapData.zoom = 2;  
          else if (maxSpan > 90) mapData.zoom = 3;   
          else if (maxSpan > 60) mapData.zoom = 4;   // Continental view
          else if (maxSpan > 30) mapData.zoom = 5;   
          else if (maxSpan > 15) mapData.zoom = 6;   // Country view
          else if (maxSpan > 8) mapData.zoom = 7;    
          else if (maxSpan > 4) mapData.zoom = 8;    // Regional view
          else if (maxSpan > 2) mapData.zoom = 9;    
          else if (maxSpan > 1) mapData.zoom = 10;   // Metropolitan area
          else if (maxSpan > 0.5) mapData.zoom = 11; 
          else if (maxSpan > 0.2) mapData.zoom = 12; // City view
          else if (maxSpan > 0.1) mapData.zoom = 13; 
          else if (maxSpan > 0.05) mapData.zoom = 14; // District view
          else if (maxSpan > 0.025) mapData.zoom = 15;
          else if (maxSpan > 0.0125) mapData.zoom = 16; // Neighborhood view
          else mapData.zoom = 17; // Street level
        } else {
          // Set default values for an empty map
          mapData.center = [0, 0];
          mapData.zoom = 2; // World view
        }
        
        return mapData;
      } catch (error) {
        console.error('Error generating map data:', error);
        return {
          center: [0, 0],
          zoom: 2,
          markers: []
        };
      }
    }
    
    // First check if we have interpretation dimensions
    if (interpretation?.dimensions) {
      const { xaxisColumn, xaxisLabel, yaxisColumn, yaxisLabel } = interpretation.dimensions;
      
      // Validate required fields - ensure we're using results from the threads API
      if (!xaxisColumn || !yaxisColumn || !rawResponse?.results?.rows?.length) {
        return null;
      }

      try {
        // Extract and parse the Y values, ensuring they are numbers
        const yValues = rawResponse.results.rows.map(item => {
          const value = Array.isArray(item) ? item[rawResponse.results.columns.indexOf(yaxisColumn)] : item[yaxisColumn];
          // Handle different types of values
          if (typeof value === 'number') return value;
          if (typeof value === 'string') {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
          }
          return 0; // Default for null/undefined
        });
        
        // Map the x and y values from the results rows (from threads API)
        // but use chart type from interpretation API
        const data = {
          labels: rawResponse.results.rows.map(item => 
            Array.isArray(item) ? item[rawResponse.results.columns.indexOf(xaxisColumn)] : item[xaxisColumn]
          ),
          datasets: [{
            label: yaxisLabel || 'Value',
            data: yValues,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1
          }],
          xaxisLabel: xaxisLabel || xaxisColumn, // Set default label to column name if not provided
          yaxisLabel: yaxisLabel || yaxisColumn,  // Set default label to column name if not provided
          suggestedChart: interpretation.suggestedChart || 'line'
        };

        return data;
      } catch (error) {
        console.error('Error generating chart data with interpretation:', error);
      }
    }
    
    // Handle case where we have suggestedChart but no dimensions
    if (interpretation?.suggestedChart && rawResponse?.results?.rows?.length > 0) {
      try {
        // Try to automatically determine x and y columns from the data
        const firstRow = rawResponse.results.rows[0];
        const columns = rawResponse.results.columns || (Array.isArray(firstRow) ? 
          Array.from({ length: firstRow.length }, (_, i) => `Column ${i+1}`) : 
          Object.keys(firstRow));
        
        // Look for date/time columns for x-axis
        const dateColumns = columns.filter(col => 
          col.toLowerCase().includes('date') || 
          col.toLowerCase().includes('time') || 
          col.toLowerCase().includes('month') || 
          col.toLowerCase().includes('year')
        );
        
        // Look for numeric columns for y-axis
        const numericColumns = columns.filter((col, index) => {
          const value = Array.isArray(firstRow) ? firstRow[index] : firstRow[col];
          return typeof value === 'number' || 
                 (typeof value === 'string' && !isNaN(parseFloat(value)));
        });
        
        // If we can't find appropriate columns, return null
        if (dateColumns.length === 0 || numericColumns.length === 0) {
          return null;
        }
        
        // Use the first date column for x-axis and first numeric column for y-axis
        const xaxisColumn = dateColumns[0];
        const yaxisColumn = numericColumns[0];
        
        // Extract and parse the Y values
        const yValues = rawResponse.results.rows.map(item => {
          const value = Array.isArray(item) ? 
            item[columns.indexOf(yaxisColumn)] : 
            item[yaxisColumn];
            
          if (typeof value === 'number') return value;
          if (typeof value === 'string') {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
          }
          return 0;
        });
        
        // Create chart data
        const data = {
          labels: rawResponse.results.rows.map(item => 
            Array.isArray(item) ? 
              item[columns.indexOf(xaxisColumn)] : 
              item[xaxisColumn]
          ),
          datasets: [{
            label: yaxisColumn,
            data: yValues,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1
          }],
          xaxisLabel: xaxisColumn,
          yaxisLabel: yaxisColumn,
          suggestedChart: interpretation.suggestedChart
        };
        
        return data;
      } catch (error) {
        console.error('Error generating chart data with auto-detected columns:', error);
      }
    }

    // Fallback to original logic if no interpretation or if interpretation failed
    const chartConfig = interpretation?.dimensions;
    if (!chartConfig || !rawResponse?.results?.rows?.length) return null;

    try {
      // Extract and parse the Y values, ensuring they are numbers
      const yValues = rawResponse.results.rows.map(item => {
        const value = Array.isArray(item) ? 
          item[rawResponse.results.columns.indexOf(chartConfig.yaxisColumn)] : 
          item[chartConfig.yaxisColumn];
          
        // Handle different types of values
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          return isNaN(parsed) ? 0 : parsed;
        }
        return 0; // Default for null/undefined
      });
      
      // Map the x and y values from the results rows
      const data = {
        labels: rawResponse.results.rows.map(item => 
          Array.isArray(item) ? 
            item[rawResponse.results.columns.indexOf(chartConfig.xaxisColumn)] : 
            item[chartConfig.xaxisColumn]
        ),
        datasets: [{
          label: chartConfig.yaxisLabel || 'Value',
          data: yValues,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        }],
        xaxisLabel: chartConfig.xaxisLabel || chartConfig.xaxisColumn, // Set default label to column name if not provided
        yaxisLabel: chartConfig.yaxisLabel || chartConfig.yaxisColumn,  // Set default label to column name if not provided
        suggestedChart: interpretation?.suggestedChart || 'line'
      };

      return data;
    } catch (error) {
      console.error('Error generating chart data:', error);
      return null;
    }
  };

  const getChartOptions = () => {
    // Get interpretation data from either location
    const interpretation = rawResponse?.interpretation || rawResponse;
    const chartConfig = interpretation?.dimensions;
    
    // If we have a suggestedChart but no dimensions, provide default options
    if (!chartConfig && interpretation?.suggestedChart) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Data Visualization'
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'X Axis'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Y Axis'
            }
          }
        }
      };
    }
    
    if (!chartConfig) return null;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Data Visualization'
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: chartConfig.xaxisLabel || chartConfig.xaxisColumn
          },
          ticks: {
            maxRotation: 45,
            minRotation: 45
          }
        },
        y: {
          title: {
            display: true,
            text: chartConfig.yaxisLabel || chartConfig.yaxisColumn
          },
          beginAtZero: true
        }
      }
    };
  };

  const getChartType = () => {
    // Get interpretation data from either location
    const interpretation = rawResponse?.interpretation || rawResponse;
    const chartType = interpretation?.suggestedChart;
    return chartType;
  };

  // Memoize chart type, data, and options to prevent unnecessary recalculations
  const chartType = useMemo(() => getChartType(), [rawResponse]);
  const chartData = useMemo(() => getChartData(), [rawResponse]);
  const chartOptions = useMemo(() => getChartOptions(), [rawResponse]);

  // Memoize map data to prevent MapView from re-rendering
  const memoizedMapData = useMemo(() => {
    if (chartType?.toLowerCase() === 'map' && chartData) {
      return chartData;
    }
    return null;
  }, [chartType, chartData]);

  if (chartType === null || !chartData) {
    return null;
  }

  // For map type, we don't need chart options
  if (chartType.toLowerCase() === 'map') {
    return (
      <div className="mt-2">
        <div className="w-full border border-gray-200 rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
          <MapView data={memoizedMapData} />
        </div>
      </div>
    );
  }

  // For other chart types, use MessageContent component
  return (
    <div className="mt-2">
      <div className="w-full h-[400px]">
        <MessageContent
          content={{
            chartType,
            data: chartData,
            options: chartOptions
          }}
          type="chart"
        />
      </div>
    </div>
  );
});

ChartSection.propTypes = {
  rawResponse: PropTypes.object.isRequired
};

export default ChartSection; 